import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Autocomplete,Button,Card,CardContent,InputAdornment,Pagination,Skeleton,Stack,TextField,Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegSquareCheck } from "react-icons/fa6";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import PlanoDeAcao from '../../service/PlanoDeAcao';
import type { analisesPendentesType,planosType } from "../../types/plano-de-acao/types";
import AnalisaPlanoModal from "./modal/Analise";

const planosService = new PlanoDeAcao();

export default function Pendentes() {
  const { userData } = useContext(UserContext);
  const [selected, setSelected] = useState<planosType | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [paginated, setPaginated] = useState<planosType[]>([]);
  const { t, i18n } = useTranslation('translation');
  const [search, setSearch] = useState("");

  function handleOpenModal() {
    setOpenModal(!openModal);
  }


  const lastPageIndex = currentPage * itemsPerPage;
  const firstPageIndex = lastPageIndex - itemsPerPage;

  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_ANALISES_PENDING'],
    queryFn: async () => {
      toast.info(t('ActionPlan.ListaAnalises.getPendentes'), { toastId: 'getPendentes' });
      const token = await AttToken();
      if (token) {
        const response: analisesPendentesType = await planosService.getPendingList(token);
        return response;
      }
    },
    enabled: userData.listaFuncionalidades.includes('plano-de-acao'),
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.planos?.length && isSuccess) {
      toast.success(`${data.planos?.length} ${t('ActionPlan.ListaAnalises.pendentesSucesso')}`, { toastId: 'successoPendente' });
    } else {
      toast.success(`${t('ActionPlan.ListaAnalises.noPendingAnalyses')}`, { toastId: 'successoPendente' });
    }
  }, [data, isSuccess, t]);

  useEffect(() => {
    if (isSuccess && !isFetching && !isLoading && data) {
      const currentPageData = data.planos.filter((ele) => ele?.nomeForm?.includes(search))
        ?.sort((a, b) => { return Number(a.dataCriacao) < Number(b.dataCriacao) ? 1 : -1; });
      setPaginated(currentPageData?.slice(firstPageIndex, lastPageIndex) ?? []);
    } if (isError) {
      toast.error(t('ActionPlan.ListaAnalises.erroPendente'), { toastId: 'erro' });
    }
  }, [firstPageIndex, lastPageIndex, data, isSuccess, isFetching, isLoading, isError, t, search]);

  const pages = [];

  if (data) {
    for (let i = 1; i <= Math.ceil(data.planos.filter((ele) => ele?.nomeForm?.includes(search))?.length / itemsPerPage); i++) {
      pages.push(i);
    }
  }

  function Loading() {
    return (
      <>
        <Skeleton height={30} width='300px' variant="rounded" />
        {Array(5).fill(0).map((_, index) => (
          <Skeleton key={index} height={150} width='100%' variant="rounded" />
        ))}
      </>

    );
  };

  if (!data
    && !isLoading
    && !isFetching
  ) return (
    <span style={{
      width: '100%',
      display: 'flex',
      justifyContent: "center",
      color: 'gray',
      fontSize: '1.5em',
      fontWeight: 600
    }}>
      {t('ActionPlan.ListaAnalises.noAnalysesOrFailedPlans')}
    </span>);

  return (
    <>
      {openModal &&
        <AnalisaPlanoModal
          openModal={openModal}
          handleOpen={handleOpenModal}
          selected={selected as planosType}
        />
      }
      <Stack
        direction='column'
        width='100%'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={2}
      >
        {isLoading || isFetching
          ? <Loading />
          : data && <>
            <Stack
              justifyContent='space-between'
              alignItems='center'
              width='100%'
              direction="row"
              spacing={1}
            >
              {<Typography textAlign='left' variant='h6' sx={{ color: '#47AA9C' }}>
                {`${data?.planos?.length ? data.planos?.length : 'Nenhuma'} ${data?.planos?.length > 1
                  ? `${t('ActionPlan.ListaAnalises.pendingAnalysesLabel').slice(0, -1)}`
                  : `${t('ActionPlan.ListaAnalises.pendingAnalysisLabel')}`}`}
              </Typography>}
              {data?.total > 5
                && <Pagination
                  style={{ display: `${!pages?.length ? 'none' : ''}` }}
                  boundaryCount={4}
                  showFirstButton
                  showLastButton
                  shape="rounded"
                  page={currentPage}
                  count={pages?.length}
                  onChange={(eve, pageNumber) => {
                    trackEventMatomo('lista analise de PA', 'click', 'button', 'muda página');
                    setCurrentPage(pageNumber);
                  }}
                />}
              {data?.total > 5
                && <Autocomplete
                  fullWidth
                  value={search ? ({ nomeForm: search } as planosType) : null}
                  getOptionLabel={option => option.nomeForm as string}
                  getOptionKey={option => option?.id as string}
                  isOptionEqualToValue={(option, value) => option.nomeForm === value.nomeForm}
                  options={data?.planos
                    ?.sort((a, b) => { return a.nomeForm?.toLowerCase() > b.nomeForm?.toLowerCase() ? 1 : - 1; })
                    ?.map((ele: planosType) => ({ ...ele })) || []}
                  onChange={(_eve, value: planosType | null, reason) => {
                    if (value?.nomeForm) {
                      setSearch(value?.nomeForm as string);
                      setCurrentPage(1);
                    }
                    else {
                      setSearch("");
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label={t('ActionPlan.PlanosDeAcaoComp.searchPlanByName')} />}
                />}
            </Stack>
            {paginated?.sort((a, b) => b?.dataCriacao - a?.dataCriacao)?.map((plano: planosType, index: number) =>
              <Card key={index} sx={{ width: '100%' }}>
                <CardContent>
                  <Stack
                    width='100%'
                    direction='row'
                    justifyContent='space-between'
                    alignItems='flex-start'
                  >
                    <Typography variant='h5' fontWeight={600} sx={{ color: '#47AA9C' }}>
                      {plano.nomeForm}
                    </Typography>
                    <Stack
                      direction='column'
                      justifyContent='flex-start'
                      alignItems='flex-end'
                      spacing={2}
                    >
                      <Button
                        variant="contained"
                        endIcon={<FaRegSquareCheck />}
                        onClick={() => {
                          trackEventMatomo('lista analise de PA', 'click', 'button', 'abre modal');
                          setOpenModal(!openModal);
                          setSelected({ ...plano });
                        }}>
                        {t('ActionPlan.ListaAnalises.analyze')}
                      </Button>
                      <TextField
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        disabled
                        variant='standard'
                        label={t('ActionPlan.ListaAnalises.formCompletionDate')}
                        value={new Date(plano.dataCriacao).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR')}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </>}
      </Stack>
    </>
  );
}
